import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Download, MapPin, Users } from 'lucide-react';
import { getApiBaseUrl } from '../../config/api';
import FitCoverImage from '../shared/FitCoverImage';
import AuthenticatedNavbar from '../headers/AuthenticatedNavbar';
import Guestheader from '../headers/guestheader';
import {
  buildCalendarLinks,
  downloadIcs,
  formatEventPlace,
  formatEventWhen,
  isEventClosed,
} from './calendarLinks';
import { downloadConfirmedAttendees } from './downloadAttendees';
import './EventAttendance.css';

export default function EventAttendanceDetails({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const API_URL = getApiBaseUrl();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams({ id: String(id) });
      if (user?.id) q.set('user_id', String(user.id));
      const res = await fetch(`${API_URL}/get_attendance_event.php?${q}`);
      const data = await res.json();
      if (!data.success || !data.event) {
        setError(data.message || 'Event not found');
        setEvent(null);
      } else {
        setEvent(data.event);
        if (data.event.is_attending) setShowCalendar(true);
      }
    } catch {
      setError('Could not load event');
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [API_URL, id, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const calLinks = useMemo(() => (event ? buildCalendarLinks(event) : null), [event]);
  const closed = event ? isEventClosed(event) : false;

  const coverUrl = (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_URL}/${String(path).replace(/^\/+/, '')}`;
  };

  const attend = async () => {
    if (!user?.id) {
      navigate('/login', { state: { from: `/event-attendance/${id}` } });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/register_attendance_event.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, event_id: Number(id) }),
      });
      const data = await res.json();
      if (data.success && data.event) {
        setEvent(data.event);
        setShowCalendar(true);
      } else {
        alert(data.message || 'Could not register attendance');
      }
    } catch {
      alert('Network error');
    } finally {
      setBusy(false);
    }
  };

  const cancelAttend = async () => {
    if (!user?.id) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/cancel_attendance_event.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, event_id: Number(id) }),
      });
      const data = await res.json();
      if (data.success && data.event) {
        setEvent(data.event);
        setShowCalendar(false);
      } else {
        alert(data.message || 'Could not cancel');
      }
    } catch {
      alert('Network error');
    } finally {
      setBusy(false);
    }
  };

  const downloadAttendees = async () => {
    if (!user?.id || !event) return;
    setDownloading(true);
    try {
      await downloadConfirmedAttendees({
        apiUrl: API_URL,
        event,
        userId: user.id,
      });
    } catch (err) {
      alert(err?.message || 'Could not download attendees.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="ead-shell">
      {user ? (
        <AuthenticatedNavbar user={user} onLogout={onLogout} />
      ) : (
        <Guestheader />
      )}
      <div className="ead-page">
        <Link to={user ? '/event-attendance' : '/'} className="ead-back">
          <ArrowLeft size={16} /> {user ? 'Back to Attend Events' : 'Back to home'}
        </Link>

        {loading ? (
          <p className="eal-empty">Loading…</p>
        ) : error || !event ? (
          <p className="eal-empty">{error || 'Event not found'}</p>
        ) : (
          <>
            {event.cover_image ? (
              <FitCoverImage
                src={coverUrl(event.cover_image)}
                alt={event.title || 'Event cover'}
                maxHeight={480}
                radius={18}
                marginBottom={18}
              />
            ) : null}
            <div className="ead-card">
              <div className="ead-title-row">
                <h1 className="ead-title">{event.title}</h1>
                {closed && <span className="ead-closed-badge">Closed</span>}
              </div>
              <div className="ead-meta-row">
                <span>
                  <Calendar size={16} /> {formatEventWhen(event)}
                </span>
                {formatEventPlace(event) && (
                  <span>
                    <MapPin size={16} /> {formatEventPlace(event)}
                  </span>
                )}
                <span>
                  <Users size={16} /> {Number(event.attendee_count || 0)} attending
                </span>
              </div>
              {event.organizer && (
                <p className="ead-organizer">Organized by {event.organizer}</p>
              )}
              {event.description && <div className="ead-desc">{event.description}</div>}

              <div className="ead-actions">
                {event.can_manage && (
                  <div className="ead-organizer-tools">
                    <button
                      type="button"
                      className="ead-download-btn"
                      disabled={downloading || Number(event.attendee_count || 0) === 0}
                      onClick={downloadAttendees}
                      title={
                        Number(event.attendee_count || 0) === 0
                          ? 'No confirmed attendees yet'
                          : 'Download confirmed attendees as CSV'
                      }
                    >
                      <Download size={16} />
                      {downloading
                        ? 'Downloading…'
                        : `Download confirmed attendees (${Number(event.attendee_count || 0)})`}
                    </button>
                  </div>
                )}
                {closed ? (
                  <>
                    <button type="button" className="ead-attend-btn is-closed" disabled>
                      Event closed
                    </button>
                    <p className="ead-hint">This event has ended and is no longer accepting RSVPs.</p>
                  </>
                ) : event.is_attending ? (
                  <>
                    <button
                      type="button"
                      className="ead-attend-btn is-attending"
                      disabled={busy}
                      onClick={cancelAttend}
                    >
                      You’re attending · Tap to cancel
                    </button>
                    <p className="ead-hint">Add this event to your personal calendar below.</p>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="ead-attend-btn"
                      disabled={busy}
                      onClick={attend}
                    >
                      {busy ? 'Saving…' : 'Attend Event'}
                    </button>
                    <p className="ead-hint">
                      {user
                        ? 'Confirm you’re going, then save it to Google, Outlook, or Yahoo Calendar.'
                        : 'Log in to attend and add this to your calendar.'}
                    </p>
                  </>
                )}

                {(showCalendar || event.is_attending) && calLinks && (
                  <div className="ead-calendar">
                    <h3>Add to calendar</h3>
                    <div className="ead-cal-links">
                      <a
                        className="ead-cal-link"
                        href={calLinks.google}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Google Calendar <span>→</span>
                      </a>
                      <a
                        className="ead-cal-link"
                        href={calLinks.outlook}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Microsoft Outlook <span>→</span>
                      </a>
                      <a
                        className="ead-cal-link"
                        href={calLinks.yahoo}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Yahoo Calendar <span>→</span>
                      </a>
                      <button
                        type="button"
                        className="ead-cal-link"
                        onClick={() => downloadIcs(event)}
                      >
                        Download .ics (Apple / other) <span>↓</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
